<template>
  <div>
    <v-sheet elevation="1">
      <v-toolbar dense color="white">
        <v-toolbar-title>{{ screenTitle }}</v-toolbar-title>
      </v-toolbar>
      <v-container fluid>
        <error-message :error-object="errorObject" />
        <success-message :success-object="successObject" />
        <p style="margin: 5px 0 15px 0">
          避難者の状況は下の表を参照してください。条件を指定することで一覧から絞り込むことができます。<br />
          検索条件の「市町村」「地区」については、以下を参考に指定してください<br />
          <span class="ml-3"
            >一時集合場所、避難退域時検査場所：所在地の市町村</span
          >
          <span class="ml-3">※地区で絞り込むことはできません</span>
          <br />
          <span class="ml-3">避難所：避難元所属市町村/地区</span>
        </p>
        <v-row>
          <v-col cols="6" md="3">
            <v-select
              v-bind:value="municipality"
              :items="municipalities"
              :rules="[rules.required]"
              label="市町村"
              placeholder="選択してください"
              clearable
              outlined
              @change="
                municipality = $event;
                changeMunicipality(municipality);
              "
              @click:clear="clearInput($event, true)"
            >
            </v-select>
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="area"
              :items="areas"
              label="地区"
              placeholder="選択してください"
              clearable
              outlined
              @change="simulate"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="facilityName"
              label="施設名"
              placeholder="入力してください"
              clearable
              append-icon="mdi-magnify"
              outlined
              counter="40"
              maxlength="40"
              :disabled="!hasResults()"
              @keyup="simulate()"
              @click:clear="simulate($event, true)"
            >
            </v-text-field>
          </v-col>
        </v-row>
        <v-row
          class="text-subtitle-2 font-weight-bold"
          v-if="evacueeStatus.hasEmergencyClassName()"
        >
          <v-col cols="12" md="2">
            {{ evacueeStatus.emergencyClassName() }}
          </v-col>
          <v-col cols="12" md="3">
            {{ evacueeStatus.datetime() }}
          </v-col>
        </v-row>
        <v-divider class="mb-1"></v-divider>
      </v-container>
      <v-container
        v-if="hasMunicipality(municipality) && evacueeStatus.hasDisaster()"
        fluid
        style="padding: 0"
      >
        <p style="margin: 5px 0 15px 15px">合計</p>
        <v-data-table
          :headers="sumHeaders"
          :items="sumItems"
          locale="ja-jp"
          hide-default-footer
          disable-sort
        >
          <template v-slot:[`item.enter_count`]="{ item }">
            {{ item.enter_count | format }}
          </template>
          <template v-slot:[`item.exit_count`]="{ item }">
            {{ item.exit_count | format }}
          </template>
        </v-data-table>
        <p style="margin: 15px 0 15px 15px">一覧</p>
        <top-operation-data-table
          :headers="facilityHeaders"
          :items="facilityList"
          locale="ja-jp"
          dispMode="nowrap"
          :sort-by="sort.by"
          :sort-desc="sort.desc"
          class="fixedWidth"
          must-sort
          ref="facilityInfoTable"
        >
          <template v-slot:top="{ pagination, options, updateOptions }">
            <v-data-footer
              :pagination="pagination"
              :options="options"
              :items-per-page-options="[5, 10, 15, -1]"
              @update:options="updateOptions"
              items-per-page-text="$vuetify.dataTable.itemsPerPageText"
            />
          </template>
          <template v-slot:[`item.fa_address`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="d-inline-block text-truncate mt-2"
                  style="max-width:720px"
                >
                  {{ item.fa_address }}
                </span>
              </template>
              <span style="max-width:70%">{{ item.fa_address }}</span>
            </v-tooltip>
          </template>
          <template v-slot:[`item.enter_count`]="{ item }">
            {{ item.enter_count | format }}
          </template>
          <template v-slot:[`item.exit_count`]="{ item }">
            {{ item.exit_count | format }}
          </template>
        </top-operation-data-table>
      </v-container>
    </v-sheet>
    <v-overlay :value="overlay" opacity="1" color="white">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import Vue from "vue";
import EvacueeStatus from "@/plugins/evacuee-status";
import { AllAreaTranslator } from "@/js/translators/allAreaTranslator";
import { ScreenId } from "@/js/value-objects/screen-id";
import messageUtils from "@/mixins/message-utils";
Vue.use(EvacueeStatus);

export default {
  mixins: [messageUtils],

  components: {
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage"),
    TopOperationDataTable: () => import("@/components/ui/TopOperationDataTable")
  },
  data: () => ({
    screenTitle: ScreenId.getScreenTitle(ScreenId.SF1301),
    overlay: false,
    municipalities: [],
    areas: [],
    facilityList: [],
    sumItems: [
      {
        enter_count: 0,
        exit_count: 0
      }
    ],
    evacueeStatus: {},
    municipality: "",
    area: "",
    facilityName: "",
    rules: {
      required: value => !!value || "必須項目です。"
    }
  }),

  computed: {
    facilityHeaders() {
      return [
        {
          text: "地域（地区）",
          align: " d-none",
          value: "eva_area_info",
          filter: this.areaFilter
        },
        {
          text: "施設名",
          value: "fa_name",
          filter: this.facilityNameFilter
        },
        {
          text: "施設住所",
          value: "fa_address",
          sortable: false
        },
        {
          text: "発災からの累計入所者数",
          value: "enter_count"
        },
        {
          text: "発災からの累計退所者数",
          value: "exit_count"
        }
      ];
    },

    sumHeaders() {
      return [
        {
          text: "発災からの累計入所者数",
          value: "enter_count"
        },
        {
          text: "発災からの累計退所者数",
          value: "exit_count"
        }
      ];
    },

    hasMunicipality: function() {
      return function(value) {
        return value;
      };
    },

    sort() {
      return {
        by: ["enter_count"],
        desc: [true]
      };
    }
  },

  watch: {
    $route: "initialize"
  },

  mounted() {
    this.pageTitle(this.screenTitle);
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      this.overlay = true;
      this.evacueeStatus = this.$evacueeStatus.factory(this.$disaster);
      await this.$store
        .dispatch("allArea/loadAllArea")
        .then(() => {
          // 市町村リストを取得
          this.municipalities = this.$store.state.allArea.municipalities;
        })
        .catch(e => {
          this.handleError(e, ScreenId.SF1301, "aggregation-menu");
        });
      this.overlay = false;
    },
    // 市町村を選択時に発火する関数
    async changeMunicipality(municipality) {
      this.overlay = true;
      // エラーメッセージのクリア
      this.clearMessage();
      // 地区の選択肢を初期化
      this.area = "";
      // 選択された市町村に紐づく地区を再検索
      this.areas = AllAreaTranslator.toAreasByMunicipality(
        this.$store.state.allArea.areas,
        municipality
      );
      if (this.evacueeStatus.hasDisaster()) {
        // 避難状況一覧で取得する施設情報はサマリーのみ
        await this.$store
          .dispatch("facilityEvaInfo/loadFacilityEvaInfo", {
            screenId: ScreenId.SF1301,
            disasterNo: this.evacueeStatus.getDisasterNo()
          })
          .then(() => {
            const ITEMS = this.$store.state.facilityEvaInfo.data.results;
            // 選択された市町村に紐づく施設リストを取得
            this.facilityList = this.$evacueeStatus.getByMunicipality(
              ITEMS,
              municipality
            );
            // STでの対応であり、修正される可能性あり
            this.facilityList.forEach(fa => {
              fa.enter_count = Number(fa.enter_count) + Number(fa.exit_count);
            });
          })
          .catch(e => {
            this.handleGetError(e);
          });
      }
      // 入退所者の合計を計算する
      this.simulate();
      // テーブルのページネーションリセット
      this.resetPage();
      this.overlay = false;
    },
    // 入退所者の合計計算関数
    simulate($event, clearFlg = false) {
      this.sumItems = [];
      let sum_enter_count = 0;
      let sum_exit_count = 0;
      /**施設名入力欄でクリア処理が行われたとき
       * ※クリア処理のイベントをトリガーにすると、入力値をクリアする前に
       * 処理が走ってしまうためこちらで空にする
       */
      if (clearFlg) {
        this.facilityName = "";
      }
      if (this.$refs.facilityInfoTable) {
        // フィルタリングの結果一覧を取得
        const FILTERED_ITEMS = this.$refs.facilityInfoTable.$children[0]
          .$children[0].filteredItems;
        FILTERED_ITEMS.forEach(item => {
          sum_enter_count += Number(item.enter_count);
          sum_exit_count += Number(item.exit_count);
        });
      }
      const SUM_ITEM = {
        enter_count: sum_enter_count,
        exit_count: sum_exit_count
      };
      this.sumItems.push(SUM_ITEM);
    },
    // 地区フィルター
    areaFilter(value) {
      this.resetPage();
      if (!this.area) {
        return true;
      }
      return value.some(area => {
        return area.eva_area_code === this.area;
      });
    },
    //施設名フィルター
    facilityNameFilter(value) {
      this.resetPage();
      if (this.facilityName == "" || this.facilityName == null) {
        return true;
      }
      // 入力文字列中の空白を削除
      const FACILITY_NAME_TEXT = this.facilityName.replace(/\s+/g, "");
      if (value.includes(FACILITY_NAME_TEXT)) {
        return true;
      }
    },
    /**検索時にページネーションをリセット
     * 初期表示ではテーブルが生成されていないためデータテーブルのメソッドが
     * 呼び出せない。生成されていることを確認してから呼びだす。
     */
    resetPage() {
      if (this.$refs.facilityInfoTable) {
        this.$refs.facilityInfoTable.resetPage();
      }
    },
    // 検索項目「施設名」の活性制御
    hasResults() {
      if (!this.municipality) {
        return false;
      }
      if (!this.facilityList.length > 0) {
        return false;
      }
      return true;
    },
    // 市町村選択肢クリア時、すべての入力項目クリア
    clearInput() {
      // 施設名のクリア
      this.facilityName = "";
    }
  },
  // テキストフォーマット
  filters: {
    // 人数データのフォーマット
    format: function(value) {
      return `${value}人`;
    }
  }
};
</script>
